import { useState } from 'react'
import {
  Box, Card, CardContent, Typography,
  TextField, Button, Alert, InputAdornment, IconButton,
} from '@mui/material'
import { Visibility, VisibilityOff, ArrowForward } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { usePlatformAdminLogin } from '../../hooks/usePlatformAdmin'

const inputSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#34A8E0' },
    '&.Mui-focused fieldset': { borderColor: '#34A8E0' },
  },
}

export default function AdminLoginPage() {
  const navigate = useNavigate()
  const loginMut = usePlatformAdminLogin()
  const [form, setForm] = useState({ email: '', password: '' })
  const [showPw, setShowPw] = useState(false)

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await loginMut.mutateAsync(form)
      navigate('/admin/dashboard')
    } catch {
      // shown via loginMut.isError
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        width: '44%', maxWidth: 680,
        background: 'linear-gradient(145deg, #082D43 0%, #0C6E9F 55%, #168FCA 100%)',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        px: 6, position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.12, backgroundImage: 'linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)', backgroundSize: '42px 42px', maskImage: 'linear-gradient(to bottom right, black, transparent 80%)' }} />
        <Box sx={{ position: 'absolute', top: '-80px', right: '-80px', width: 320, height: 320, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)' }} />
        <Box sx={{ position: 'absolute', bottom: '-120px', left: '-60px', width: 400, height: 400, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)' }} />
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box sx={{ width: 90, height: 90, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.16)', display: 'grid', placeItems: 'center', mx: 'auto', mb: 3, boxShadow: '0 20px 40px rgba(1,18,28,.18)' }}>
            <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 66, height: 66 }} />
          </Box>
          <Typography variant="h4" fontWeight={800} color="white" sx={{ letterSpacing: '-0.02em', mb: 1.5 }}>
            OmniTrace
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400, fontSize: 16, lineHeight: 1.6 }}>
            Platform Administration Console
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 1.5, textAlign: 'left' }}>
            {['Cross-tenant management', 'Dynamic billing & plans', 'Centralised governance'].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.6)', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13.5 }}>{item}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 2, sm: 5 }, py: 4 }}>
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 3 }}>
            <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 56, height: 56, mb: 1.5 }} />
            <Typography variant="h5" fontWeight={800} color="#0f172a">OmniTrace Admin</Typography>
          </Box>

          <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ mb: 0.5, letterSpacing: '-0.02em', display: { xs: 'none', md: 'block' } }}>
            Welcome back
          </Typography>
          <Typography variant="body2" color="#64748b" sx={{ mb: 3, display: { xs: 'none', md: 'block' } }}>
            Sign in to access the admin console
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, mb: 2.5, borderRadius: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 600, fontSize: 11 }}>
              Restricted access — authorised personnel only
            </Typography>
          </Box>

          {loginMut.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(loginMut.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Invalid credentials'}
            </Alert>
          )}

          <Card sx={{ boxShadow: '0 18px 55px rgba(16,42,67,0.1)' }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Box component="form" onSubmit={handlePasswordSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 0.75 }}>Email Address</Typography>
                  <TextField type="email" value={form.email} required fullWidth autoFocus size="small" sx={inputSx}
                    autoComplete="username"
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 0.75 }}>Password</Typography>
                  <TextField type={showPw ? 'text' : 'password'} value={form.password} required fullWidth size="small" sx={inputSx}
                    autoComplete="current-password"
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label={showPw ? 'Hide password' : 'Show password'} onClick={() => setShowPw((p) => !p)} edge="end" size="small">
                          {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )}} />
                </Box>
                <Button type="submit" variant="contained" size="large" fullWidth
                  disabled={loginMut.isPending} endIcon={<ArrowForward />}
                  sx={{ mt: 0.5, py: 1.375, fontSize: 14 }}>
                  {loginMut.isPending ? 'Signing in…' : 'Sign In'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  )
}
